/*
eWorldES Proprietary Software
Copyright (C) 2025 eWorldES. All rights reserved.
 
This software is proprietary to eWorldES and is protected by copyright laws and international treaties.
You may not use, copy, modify, distribute, or disclose this software without explicit written permission from eWorldES.
Any unauthorized use or reproduction of this software is strictly prohibited and may result in legal action.
 
License Agreement:
By using this software, you agree to be bound by the terms of the eWorldES Proprietary Software License Agreement.
For a copy of the agreement, visit https://eworldes.com/ or contact eWorldES directly for more information.
 
This software is provided "AS IS" without warranty of any kind, express or implied, including but not limited to the warranties of
merchantability, fitness for a particular purpose, or non-infringement. In no event shall eWorldES be liable for any damages
arising out of the use of this software, even if eWorldES has been advised of the possibility of such damages.
 
For inquiries or requests regarding this software, please contact eWorldES at:
Email: info@eworldes.com
Website: https://eworldes.com/
*/

import { useRef, useEffect, useState } from "react";
import {
    Box,
    Paper,
    Avatar,
    Stack,
    Typography,
} from "eworldes-ui-toolkit-v1.1.0";
import { styled } from "@mui/material/styles";
import PersonIcon from "@mui/icons-material/Person";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { ImageDialog } from "./ImageDialog";
import { ReferenceDialog } from "./ReferenceDialog";
import colors from "theme/colors";

const MessageItem = styled(Paper)<{ user: string }>(({ theme, user }) => ({
    paddingInline: theme.spacing(2),
    paddingBlock: theme.spacing(1),
    textAlign: "left",
    borderRadius: "20px",
    textWrap: "pretty",
    color: user === "user" ? colors.lco.white : colors.lco.text.primary,
    marginBottom: theme.spacing(2),
    maxWidth: "75%",
    overflowWrap: "break-word",
    fontSize: "1rem",
    lineHeight: "1.4rem",
    float: user === "user" ? "right" : "left",
    backgroundColor: user === "user" ? "#BA68C8" : "#f5f5f5",
}));

// Basic Markdown-like parser for bold and references
const SimpleMarkdown = ({ text, onReferenceClick, onImageClick }: any) => {
    if (!text) return null;

    // Split by potential images first: ![alt](url)
    const imageRegex = /!\[(.*?)\]\((.*?)\)/g;
    const parts = text.split(imageRegex);

    const renderedParts = [];
    for (let i = 0; i < parts.length; i++) {
        if (i % 3 === 0) {
            // Regular text (might contain bold or backticks)
            const subParts = parts[i].split(/(\*\*.*?\*\*|`.*?`|###.*?$|\n)/m);
            renderedParts.push(
                ...subParts.map((part: string, index: number) => {
                    if (part.startsWith("**") && part.endsWith("**")) {
                        return <strong key={index}>{part.slice(2, -2)}</strong>;
                    }
                    if (part.startsWith("`") && part.endsWith("`")) {
                        const code = part.slice(1, -1);
                        return (
                            <span
                                key={index}
                                onClick={() => onReferenceClick(code)}
                                style={{
                                    cursor: "pointer",
                                    textDecoration: "underline",
                                    color: colors.lco.link,
                                    fontFamily: "monospace",
                                    backgroundColor: "rgba(0,0,0,0.05)",
                                    padding: "0 4px",
                                    borderRadius: "4px",
                                }}
                            >
                                {code}
                            </span>
                        );
                    }
                    if (part.startsWith("###")) {
                        return (
                            <Typography
                                key={index}
                                variant="h6"
                                sx={{ mt: 1, fontWeight: "bold" }}
                            >
                                {part.replace("###", "").trim()}
                            </Typography>
                        );
                    }
                    if (part === "\n") {
                        return <br key={index} />;
                    }
                    return part;
                })
            );
        } else if (i % 3 === 1) {
            // Alt text - ignored or used for tooltip
        } else {
            // Image URL
            const url = parts[i];
            renderedParts.push(
                <Box
                    key={i}
                    sx={{ width: "100%", cursor: "pointer", borderRadius: "8px", my: 1 }}
                >
                    <img
                        src={url}
                        alt="Chat image"
                        onClick={() => onImageClick(url)}
                        style={{ width: "100%", borderRadius: "8px" }}
                    />
                </Box>
            );
        }
    }

    return (
        <Box sx={{ fontSize: "0.9rem", fontFamily: "sans-serif" }}>
            {renderedParts}
        </Box>
    );
};

export const Messages = ({ messages }: { messages: any[] }) => {
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const [modalImage, setModalImage] = useState<string | null>(null);
    const [modalReference, setModalReference] = useState<string | null>(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleImageClick = (src: string) => {
        setModalImage(src);
    };

    const handleReferenceClick = (src: string) => {
        setModalReference(src);
    };

    const handleCloseModal = () => {
        setModalImage(null);
        setModalReference(null);
    };

    return (
        <>
            <Stack spacing={2}>
                {messages.map((message, index) => {
                    const { type, text } = message;
                    return (
                        <Box key={index} sx={{ width: "100%", clear: "both" }}>
                            <Avatar
                                sx={{
                                    float: type === "user" ? "right" : "left",
                                    mb: 1,
                                    mx: 1,
                                    bgcolor:
                                        type === "user"
                                            ? colors.lco.primary.hover
                                            : colors.lco.neutral.main,
                                }}
                            >
                                {type === "user" ? <PersonIcon /> : <SmartToyIcon />}
                            </Avatar>
                            <MessageItem elevation={0} user={type}>
                                {type === "user" ? (
                                    <Typography variant="body2">{text}</Typography>
                                ) : (
                                    <SimpleMarkdown
                                        text={text}
                                        onReferenceClick={handleReferenceClick}
                                        onImageClick={handleImageClick}
                                    />
                                )}
                            </MessageItem>
                        </Box>
                    );
                })}
                <div ref={messagesEndRef}></div>
            </Stack>
            <ImageDialog imageUrl={modalImage} onClose={handleCloseModal} />
            <ReferenceDialog
                referenceText={modalReference}
                onClose={handleCloseModal}
            />
        </>
    );
};
